import React from 'react';
import { ESG_PLATFORM_LOGO_SRC, reportMuted } from '../../constants/reportTheme';

export type ReportChromeSize = 'default' | 'compact';

const coverLogoHeight: Record<ReportChromeSize, number> = {
  default: 88,
  compact: 72,
};

const coverLogoMaxWidth: Record<ReportChromeSize, number> = {
  default: 320,
  compact: 280,
};

const coverLogoMargin: Record<ReportChromeSize, number> = {
  default: 40,
  compact: 28,
};

const coverContentOffsetTop: Record<ReportChromeSize, number> = {
  default: 150,
  compact: 110,
};

const headerPlatformLogoHeight: Record<ReportChromeSize, number> = {
  default: 44,
  compact: 36,
};

export function ReportFooter({
  periodLabel,
  generatedLabel,
  pageIndex,
  totalPages,
  compact = false,
}: {
  periodLabel: string;
  generatedLabel: string;
  pageIndex: number;
  totalPages: number;
  compact?: boolean;
}) {
  return (
    <footer
      className="esg-report-footer esg-report-avoid-break"
      style={{
        borderTop: '1px solid #e2e8f0',
        paddingTop: compact ? 10 : 12,
        marginTop: 'auto',
        fontSize: '8pt',
        color: '#64748b',
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, color: '#334155' }}>Generated by RECYCLEE ESG</div>
      <div>Reporting period: {periodLabel}</div>
      <div>Generated: {generatedLabel}</div>
      <div style={{ marginTop: compact ? 6 : 8, textAlign: 'right', color: '#475569' }}>
        Page {pageIndex} of {totalPages}
      </div>
    </footer>
  );
}

export function ReportPageShell({
  children,
  periodLabel,
  generatedLabel,
  pageIndex,
  totalPages,
  methodologyFooter,
  compactFooter = false,
}: {
  children: React.ReactNode;
  periodLabel: string;
  generatedLabel: string;
  pageIndex: number;
  totalPages: number;
  methodologyFooter?: React.ReactNode;
  compactFooter?: boolean;
}) {
  return (
    <div className="esg-report-page">
      <div className="esg-report-page-inner">
        <div className="esg-report-page-main">
          {children}
          {methodologyFooter}
        </div>
        <ReportFooter
          periodLabel={periodLabel}
          generatedLabel={generatedLabel}
          pageIndex={pageIndex}
          totalPages={totalPages}
          compact={compactFooter}
        />
      </div>
    </div>
  );
}

function PlatformLogoRow({
  organizationLogoSrc,
  size,
}: {
  organizationLogoSrc: string | null;
  size: ReportChromeSize;
}) {
  const platformHeight = headerPlatformLogoHeight[size];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, height: '100%' }}>
      <img
        src={ESG_PLATFORM_LOGO_SRC}
        alt="RECYCLEE ESG"
        height={platformHeight}
        style={{ height: platformHeight, width: 'auto' }}
      />
      {organizationLogoSrc ? (
        <>
          <div
            style={{
              width: 1,
              alignSelf: 'stretch',
              background: '#e2e8f0',
              flexShrink: 0,
            }}
          />
          <img
            src={organizationLogoSrc}
            alt=""
            crossOrigin="anonymous"
            style={{
              height: platformHeight,
              width: 'auto',
              maxWidth: platformHeight * 2.5,
              objectFit: 'contain',
            }}
          />
        </>
      ) : null}
    </div>
  );
}

export function ReportCoverPage({
  reportTitle,
  organizationName,
  periodLabel,
  generatedLabel,
  organizationLogoSrc,
  size = 'default',
}: {
  reportTitle: string;
  organizationName: string;
  periodLabel: string;
  generatedLabel: string;
  organizationLogoSrc: string | null;
  size?: ReportChromeSize;
}) {
  const padding = size === 'compact' ? '24px 32px 32px' : '32px 40px 48px';
  const titleSize = size === 'compact' ? '20pt' : '22pt';
  const titleMargin = size === 'compact' ? '0 0 20px' : '0 0 28px';
  const metaMargin = size === 'compact' ? 12 : 16;
  const coverLogoSrc = organizationLogoSrc ?? ESG_PLATFORM_LOGO_SRC;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'center',
        padding,
        paddingTop: coverContentOffsetTop[size],
      }}
    >
      <img
        src={coverLogoSrc}
        alt=""
        crossOrigin={organizationLogoSrc ? 'anonymous' : undefined}
        height={coverLogoHeight[size]}
        style={{
          height: coverLogoHeight[size],
          width: 'auto',
          maxWidth: coverLogoMaxWidth[size],
          objectFit: 'contain',
          marginBottom: coverLogoMargin[size],
        }}
      />
      <h1
        style={{
          fontSize: titleSize,
          fontWeight: 700,
          margin: titleMargin,
          color: '#0f172a',
          letterSpacing: '-0.02em',
          maxWidth: 520,
          lineHeight: 1.25,
        }}
      >
        {reportTitle}
      </h1>
      <div style={{ ...reportMuted, fontSize: '10pt', lineHeight: size === 'compact' ? 1.75 : 1.85 }}>
        <div style={{ fontWeight: 600, color: '#334155', fontSize: '11pt', marginBottom: 4 }}>
          {organizationName || 'Organization'}
        </div>
        <div style={{ marginTop: metaMargin }}>Reporting period: {periodLabel}</div>
        <div>Generated: {generatedLabel}</div>
      </div>
    </div>
  );
}

export function ReportContentHeader({
  title,
  organizationName,
  periodLabel,
  generatedLabel,
  organizationLogoSrc,
  size = 'default',
  showSummaryNote = false,
}: {
  title: string;
  organizationName: string;
  periodLabel: string;
  generatedLabel: string;
  organizationLogoSrc: string | null;
  size?: ReportChromeSize;
  showSummaryNote?: boolean;
}) {
  const isCompact = size === 'compact';
  const metaFontSize = isCompact ? '8pt' : undefined;
  const orgNameFontSize = isCompact ? '9pt' : '10pt';
  const titleFontSize = isCompact ? '15pt' : '18pt';
  const titleMargin = isCompact ? '12px 0 0' : '18px 0 0';

  return (
    <header className="esg-report-avoid-break" style={{ marginBottom: isCompact ? 14 : 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          gap: isCompact ? 12 : 16,
          paddingBottom: isCompact ? 12 : 16,
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        <PlatformLogoRow organizationLogoSrc={organizationLogoSrc} size={size} />
        <div
          style={{
            ...reportMuted,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'right',
            lineHeight: isCompact ? 1.45 : 1.55,
            fontSize: metaFontSize,
          }}
        >
          <div style={{ fontWeight: 600, color: '#334155', fontSize: orgNameFontSize }}>
            {organizationName || 'Organization'}
          </div>
          <div>Reporting period: {periodLabel}</div>
          <div>Generated: {generatedLabel}</div>
        </div>
      </div>
      <h1
        style={{
          fontSize: titleFontSize,
          fontWeight: 700,
          margin: titleMargin,
          color: '#0f172a',
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </h1>
      {showSummaryNote ? (
        <p style={{ ...reportMuted, marginTop: 10, maxWidth: '100%' }}>
          Summary of indicators recorded in Recyclee ESG for this period. Intended for stakeholders; not a certification
          or statutory compliance statement.
        </p>
      ) : null}
    </header>
  );
}
